<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'AgreementPrint',
})
</script>
<template>
  <BdBtn :disabled="disabled" :icon="PrinterOutlined" @click="open" type="primary">生成/下载协议</BdBtn>
  <Modal
    title="选择协议类型"
    v-model:visible="visible"
    width="600px"
    centered
    destroy-on-close
    @afterClose="resetState"
    @ok="handleOk"
    :confirm-loading="okLoading"
  >
    <div :class="bem()">
      <Radio.Group v-model:value="type" style="width: 100%">
        <div :class="bem('box')">
          <template v-for="item in part1Options">
            <Radio v-if="item" :key="item.value" style="display: flex" :value="item.value">
              {{ item.label }}
            </Radio>
            <div v-else></div>
          </template>
        </div>
        <Divider />
        <Radio v-for="item in part2Options" :key="item.value" :value="item.value">
          {{ item.label }}
        </Radio>
      </Radio.Group>
      <template v-if="showHouseSelect">
        <Divider style="margin: 12px 0" />
        <HouseSelect :id="id!" v-model:value="houseIds" />
      </template>
    </div>
  </Modal>
  <Preview ref="previewRef" />
</template>

<script lang="ts" setup name="AgreementPrint">
import { computed, ref } from 'vue'
import { PrinterOutlined } from '@ant-design/icons-vue'
import { createNamespace } from '@bd/vue3-utils'
import { Divider, message, Modal, Radio } from 'ant-design-vue'
import { BdBtn } from '-/Common/BdBtn'
import { JT_PROJ_TYPE, JT_ZCH_AGREEMENT_TYPE, JT_ZCH_AGREEMENT_TYPE_LABEL, JT_ZCH_AGREEMENT_TYPE_OPTIONS } from '@/constant/biz'
import {
  API_JT_ZCH_PRINT_1,
  API_JT_ZCH_PRINT_2,
  API_JT_ZCH_PRINT_3,
  API_JT_ZCH_PRINT_4_V2,
  API_JT_ZCH_PRINT_5,
  API_JT_ZCH_PRINT_6,
  API_JT_ZCH_PRINT_7,
  API_JT_ZCH_PRINT_8,
  API_JT_ZCH_PRINT_9,
  API_JT_ZCH_PRINT_10
} from '@/api/jtZch'
import Preview from './Preview.vue'
import HouseSelect from './HouseSelect.vue'
import { genFileDownUrl } from '@/utils'
import { useRoute } from 'vue-router'
const props = withDefaults(
  defineProps<{
    id?: string
    projType?: number
    disabled?: boolean
  }>(),
  {
    projType: JT_PROJ_TYPE.CITY
  }
)
const route = useRoute()
const [_, bem] = createNamespace('AgreementPrint')

const filterOptions = (options: any[], allowValues: string[]) => options.filter(item => allowValues.includes(item.value as string))

/** 弹窗是否可见 */
const visible = ref(false)

/** 当前选中的协议类型 */
const type = ref<string>()

/** 选中的房屋ID */
const houseIds = ref<string[]>([])

/** 是否展示房屋选择 */
const showHouseSelect = computed(() => type.value === JT_ZCH_AGREEMENT_TYPE.TICKET_CONFIRMATION)

/** 打开弹窗 */
const open = () => {
  visible.value = true
}

/** 重置弹窗内状态（通常在 afterClose 里调用） */
const resetState = () => {
  type.value = undefined
  houseIds.value = []
}

// 选项分组：按业务含义拆成两段展示。
const PART1_CITY_VALUES: any = [
  JT_ZCH_AGREEMENT_TYPE.CITY_COMPENSATION,
  JT_ZCH_AGREEMENT_TYPE.CITY_RELOCATION_COMPENSATION,
  JT_ZCH_AGREEMENT_TYPE.CITY_TICKET_SUPPLEMENT
]

const PART1_TOWN_VALUES: any = [
  JT_ZCH_AGREEMENT_TYPE.TOWN_COMPENSATION_TICKET,
  JT_ZCH_AGREEMENT_TYPE.TOWN_TICKET_RELOCATION_COMPENSATION,
  JT_ZCH_AGREEMENT_TYPE.TOWN_COMPENSATION,
  JT_ZCH_AGREEMENT_TYPE.TOWN_RELOCATION_COMPENSATION,
  JT_ZCH_AGREEMENT_TYPE.TOWN_TICKET_SUPPLEMENT,
  JT_ZCH_AGREEMENT_TYPE.CITY_TICKET_RELOCATION_SUPPLEMENT
]

const PART2_VALUES = [JT_ZCH_AGREEMENT_TYPE.TICKET_NOTICE, JT_ZCH_AGREEMENT_TYPE.TICKET_CONFIRMATION, JT_ZCH_AGREEMENT_TYPE.GIVE_UP_RESIDUAL_VALUE]

/** 协议类型选项（第一段） */
const part1Options = computed(() =>
  (props.projType === JT_PROJ_TYPE.CITY ? PART1_CITY_VALUES : PART1_TOWN_VALUES).map(value =>
    value === false ? false : { value, label: JT_ZCH_AGREEMENT_TYPE_LABEL[value] }
  )
)
// filterOptions(JT_ZCH_AGREEMENT_TYPE_OPTIONS, props.projType === JT_PROJ_TYPE.CITY ? PART1_CITY_VALUES : PART1_TOWN_VALUES)

/** 协议类型选项（第二段） */
const part2Options = computed(() => filterOptions(JT_ZCH_AGREEMENT_TYPE_OPTIONS, PART2_VALUES))
const previewRef = ref()
const okLoading = ref(false)

const FETCH_MAP = {
  [JT_ZCH_AGREEMENT_TYPE.TOWN_COMPENSATION_TICKET]: API_JT_ZCH_PRINT_2,
  [JT_ZCH_AGREEMENT_TYPE.TOWN_COMPENSATION]: API_JT_ZCH_PRINT_3,
  [JT_ZCH_AGREEMENT_TYPE.CITY_COMPENSATION]: API_JT_ZCH_PRINT_1,
  [JT_ZCH_AGREEMENT_TYPE.TICKET_NOTICE]: API_JT_ZCH_PRINT_5,
  [JT_ZCH_AGREEMENT_TYPE.GIVE_UP_RESIDUAL_VALUE]: API_JT_ZCH_PRINT_6,
  [JT_ZCH_AGREEMENT_TYPE.TOWN_TICKET_SUPPLEMENT]: API_JT_ZCH_PRINT_7,
  [JT_ZCH_AGREEMENT_TYPE.CITY_TICKET_SUPPLEMENT]: API_JT_ZCH_PRINT_7,
  [JT_ZCH_AGREEMENT_TYPE.TOWN_RELOCATION_COMPENSATION]: API_JT_ZCH_PRINT_8,
  [JT_ZCH_AGREEMENT_TYPE.CITY_RELOCATION_COMPENSATION]: API_JT_ZCH_PRINT_9,
  [JT_ZCH_AGREEMENT_TYPE.TOWN_TICKET_RELOCATION_COMPENSATION]: API_JT_ZCH_PRINT_10,
  [JT_ZCH_AGREEMENT_TYPE.CITY_TICKET_RELOCATION_SUPPLEMENT]: API_JT_ZCH_PRINT_7
}

const handleOk = async () => {
  const currentType = type.value!

  // 房票安置价款确认书需要选择房屋
  const isTicketConfirmation = currentType === JT_ZCH_AGREEMENT_TYPE.TICKET_CONFIRMATION
  if (isTicketConfirmation && houseIds.value.length === 0) {
    message.warning('请选择房屋')
    return
  }

  const fetchFn = isTicketConfirmation ? undefined : FETCH_MAP[currentType]
  if (!fetchFn && !isTicketConfirmation) {
    message.error('获取协议失败，请稍后重试')
    return
  }

  okLoading.value = true
  try {
    if (isTicketConfirmation) {
      const { downLoadUrl } = await API_JT_ZCH_PRINT_4_V2({
        id: props.id!,
        ids: props.id!,
        projectId: route.params.pId as string,
        houseIds: houseIds.value
      })
      const urls = downLoadUrl.map(genFileDownUrl)
      previewRef.value.open(urls)
    } else {
      const { downLoadUrl } = await fetchFn!(props.id)
      const url = genFileDownUrl(downLoadUrl)
      previewRef.value.open(url)
    }
    visible.value = false
  } catch (error) {
    message.error('获取协议失败，请稍后重试')
  } finally {
    okLoading.value = false
  }
}
</script>
<style lang="less">
.agreement-print {
  &__box {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}
</style>
